import React, { useState, useEffect } from 'react';
import { CloudSun, RefreshCw, AlertCircle } from 'lucide-react';

export function FetchData() {
  const [forecasts, setForecasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const populateWeatherData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('weatherforecast');
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      const data = await response.json();
      setForecasts(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch weather telemetry');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    populateWeatherData();
  }, []);

  const getSummaryBadgeClass = (summary) => {
    const s = (summary || '').toLowerCase();
    if (s.includes('hot') || s.includes('warm') || s.includes('scorching') || s.includes('sweltering')) {
      return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
    }
    if (s.includes('cold') || s.includes('freezing') || s.includes('chilly') || s.includes('cool')) {
      return 'text-sky-400 bg-sky-500/10 border-sky-500/30';
    }
    return 'text-indigo-400 bg-indigo-500/10 border-indigo-500/30';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded bg-indigo-500/10 border border-indigo-500/30 text-indigo-400">
            <CloudSun className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-100" id="tableLabel">
              Weather Telemetry Stream
            </h1>
            <p className="text-xs text-slate-400">
              Demonstrates Minimal API routing and JSON response deserialization from the ASP.NET backend.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={populateWeatherData}
          disabled={loading}
          className="inline-flex items-center space-x-1.5 rounded-md px-3.5 py-1.5 text-xs font-medium uppercase tracking-wide bg-[#1a1d27] hover:bg-slate-800 text-slate-200 border border-slate-700 transition-colors disabled:opacity-50 self-start sm:self-auto"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-rose-500/10 border border-rose-500/30 p-4 text-xs text-rose-400 flex items-center space-x-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>Error loading telemetry: {error}</span>
        </div>
      )}

      <div className="bg-[#11131a] border border-slate-800 rounded-md overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 text-center space-y-3">
            <div className="inline-block animate-spin text-indigo-400">
              <RefreshCw className="w-6 h-6" />
            </div>
            <p className="text-xs font-mono text-slate-400">Fetching telemetry from backend API...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-800" aria-labelledby="tableLabel">
              <thead className="bg-[#1a1d27]">
                <tr>
                  <th scope="col" className="px-5 py-3 text-left text-[11px] font-semibold font-mono text-slate-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-5 py-3 text-left text-[11px] font-semibold font-mono text-slate-400 uppercase tracking-wider">
                    Temp (C)
                  </th>
                  <th scope="col" className="px-5 py-3 text-left text-[11px] font-semibold font-mono text-slate-400 uppercase tracking-wider">
                    Temp (F)
                  </th>
                  <th scope="col" className="px-5 py-3 text-left text-[11px] font-semibold font-mono text-slate-400 uppercase tracking-wider">
                    Summary Condition
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/80 bg-[#11131a] text-xs">
                {forecasts.map((forecast) => (
                  <tr key={forecast.date} className="hover:bg-[#1a1d27]/40 transition-colors">
                    <td className="px-5 py-3.5 whitespace-nowrap font-mono text-slate-300">
                      {forecast.date}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap font-mono text-slate-200">
                      {forecast.temperatureC}°C
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap font-mono text-slate-400">
                      {forecast.temperatureF}°F
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${getSummaryBadgeClass(forecast.summary)}`}>
                        {forecast.summary}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
